import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Where a tap already does something, so a tap cannot also mean "tell me what this says".
 *
 * `[data-tap-action]` is for the containers that are clickable without being a control — a
 * table row that opens its record. Without it, tapping a truncated name in a row would navigate
 * AND flash that name's bubble over the page being left.
 */
const INTERACTIVE =
  'a[href], button, input, select, textarea, label, summary, [contenteditable], [role="button"], [role="link"], [role="option"], [role="tab"], [role="menuitem"], [role="checkbox"], [role="switch"], [data-tap-action]';

/** Clear of the screen edge — the same 16px gutter the pages keep. */
const GUTTER = 16;
const GAP = 8;

interface Shown {
  holder: Element;
  text: string;
}

/**
 * A `title` attribute, readable on a touch screen. Mounted once, in `main.tsx`.
 *
 * The console puts real information in `title`: what an arrears stage means, the full text of a
 * name the column truncated, the note on a property, how a bond's due date was estimated. With a
 * mouse that is a hover away. A phone has no hover and no browser shows `title` on a tap, so all
 * of it was unreachable — not wrong, just missing, which nobody at a desk would ever notice.
 *
 * ── ONE LISTENER, NOT A TOOLTIP PER CALL SITE ─────────────────────────────────────────────────
 *
 * There are dozens of these and new ones arrive with every column. Converting each to a tooltip
 * component is a convention every future one has to remember; this makes the attribute itself
 * work, the way the 16px input floor in `tokens.css` makes every field safe from iOS zoom
 * without anyone opting in.
 *
 * Tapping text that has a `title` shows it in a bubble; the next tap anywhere, a scroll or
 * Escape puts it away. Only TOUCH and PEN taps — a mouse already has the native tooltip, and
 * two of them would be one too many.
 *
 * ── WHAT IT LEAVES ALONE ──────────────────────────────────────────────────────────────────────
 *
 * Anything interactive. On a button or a link the tap already does something, and there the
 * title is usually a label the text beside it repeats. A control whose `title` carries
 * something the screen does not otherwise say has to show it itself on a coarse pointer — the
 * trigger list and the inspection condition buttons do.
 *
 * ── NOT INTERACTIVE ITSELF ────────────────────────────────────────────────────────────────────
 *
 * `pointer-events: none`, so the bubble can never be the thing a tap lands on. That also keeps
 * a Radix dialog from treating a tap on it as a press outside and closing, and `z-[75]` puts it
 * above every dialog and the phone's notification sheet (`z-[70]`) and under the error toasts
 * (`z-[80]`). `aria-hidden`, because a screen reader already announces a `title` as the
 * element's description — this would be a second copy.
 */
export function TouchTitles() {
  const [shown, setShown] = useState<Shown | null>(null);

  useEffect(() => {
    let touch = false;
    let current: Element | null = null;
    const hide = () => {
      current = null;
      setShown(null);
    };

    const onPointerDown = (event: PointerEvent) => {
      touch = event.pointerType !== 'mouse';
    };
    const onClick = (event: MouseEvent) => {
      if (!touch) return;
      const target = event.target instanceof Element ? event.target : null;
      const holder = target?.closest('[title]');
      const text = holder?.getAttribute('title')?.trim();
      if (!target || !holder || !text || target.closest(INTERACTIVE) || holder === current) {
        // Tapping the same text again puts it away rather than redrawing it in place.
        hide();
        return;
      }
      current = holder;
      setShown({ holder, text });
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    // Capture, so a scroll inside any container — a table, the queue, a dialog — counts, not
    // only the page's. The bubble is fixed to where the text WAS.
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, []);

  return shown ? createPortal(<Bubble key={shown.text} {...shown} />, document.body) : null;
}

function Bubble({ holder, text }: Shown) {
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);

  // Measured before paint, so the bubble is never seen at the origin and then jumping.
  useLayoutEffect(() => {
    const bubble = ref.current;
    if (!bubble) return;
    const at = holder.getBoundingClientRect();
    const { width, height } = bubble.getBoundingClientRect();
    const centred = at.left + at.width / 2 - width / 2;
    const left = Math.max(GUTTER, Math.min(centred, window.innerWidth - GUTTER - width));
    // Above the text, where a finger is not; below only when the top of the screen is in the way.
    const above = at.top - GAP - height;
    setPlace({ top: above >= GUTTER ? above : at.bottom + GAP, left });
  }, [holder]);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      style={place ?? { top: 0, left: 0, visibility: 'hidden' }}
      className="pointer-events-none fixed z-[75] max-w-[min(20rem,calc(100vw-2rem))] rounded-md bg-surface px-3 py-2 text-[0.8125rem] break-words text-ink shadow-pop ring-1 ring-rule"
    >
      {text}
    </div>
  );
}
